import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'reactstrap';
import { Drawer } from '../ui/Drawer';
import { StepModeSelect, type SplitMode } from './wizard/StepModeSelect';
import { WizardSteps } from './wizard/WizardSteps';
import { ManualModeKanban } from './wizard/ManualModeKanban';
import { AutoQtyOperatorPicker } from './wizard/AutoQtyOperatorPicker';
import {
  CriteriaSegmentInput, DEFAULT_CRITERIA_DRAFT, buildCriteriaCmd, simulateCriteria,
  type CriteriaDraft,
} from './wizard/CriteriaSegmentInput';
import { StepConfirmSplit, type ConfirmSummaryRow } from './wizard/StepConfirmSplit';
import { useSplitPickList, type SplitCmd } from '../../api/pickListSplit';
import { useTenantContext } from '../../auth/useTenantContext';
import { useAssignmentBoard } from '../../api/assignments';
import type { PickListDetail } from '../../api/pickLists';

/**
 * Turn a failed split mutation into an Italian, user-facing message. The error
 * thrown by useSplitPickList carries `status` and a parsed problem+json `body`
 * ({ code, detail }). 403 is the common case (caller lacks warehouse-admin), so
 * it gets a dedicated, actionable hint.
 */
function splitErrorMessage(err: unknown, t: (k: string) => string): string {
  const e = err as { status?: number; body?: { detail?: string } | null; message?: string } | null;
  if (e?.status === 403) {
    return t('wizard.errors.forbidden');
  }
  const detail = e?.body?.detail;
  if (typeof detail === 'string' && detail.trim() !== '') return detail;
  return e?.message ?? t('wizard.errors.generic');
}

export interface PickListSplitWizardProps {
  isOpen: boolean;
  onClose: () => void;
  detail: PickListDetail;
}

export function PickListSplitWizard({ isOpen, onClose, detail }: PickListSplitWizardProps) {
  const { t } = useTranslation('picklist');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [mode, setMode] = useState<SplitMode | null>(null);
  // mode-specific drafts (populated in E2/E3/E4)
  const [autoQtyOps, setAutoQtyOps] = useState<number[]>([]);
  // seq → list of partial assignments. Empty/missing ⇒ unassigned (full qty
  // free). Single entry with qty=qty_required ⇒ whole-row assignment (the
  // common case; what plain drag&drop produces). Multiple entries ⇒ split
  // across operators via the SplitRowDialog modal.
  const [manualAssignments, setManualAssignments] =
    useState<Record<number, Array<{ operator_user_id: number; qty: number }>>>({});
  const [criteriaDraft, setCriteriaDraft] = useState<CriteriaDraft>(DEFAULT_CRITERIA_DRAFT);

  const { token, tenantCode } = useTenantContext();
  const split = useSplitPickList(token, tenantCode, detail.pick_list.id);

  // Operators for the split columns come from the assignment-board endpoint
  // (GET /admin/assignments), which warehouse-admin CAN read and which already
  // returns only the warehouse's voice-operators (server-side scoped by role +
  // warehouse). The previous source (GET /admin/users) is tenant-admin-only, so
  // a warehouse-admin got a 403 and the wizard rendered no operator columns.
  const boardQ = useAssignmentBoard(token, tenantCode, detail.pick_list.warehouse_id);
  const availableOperators = (boardQ.data?.operators ?? [])
    .map((op) => ({ id: op.user_id, label: op.label }));

  function reset(): void {
    setStep(1); setMode(null); setAutoQtyOps([]); setManualAssignments({});
    setCriteriaDraft(DEFAULT_CRITERIA_DRAFT);
  }

  // SP-8g §12 — riepilogo lista in testa al wizard (aiuta a decidere come
  // splittare): totale quantità + ubicazioni distinte, derivati dagli items.
  const totalQtyRequired = detail.items.reduce((s, it) => s + it.qty_required, 0);
  const distinctLocations = new Set(
    detail.items.map((it) => it.location_code ?? '').filter((l) => l !== ''),
  ).size;

  // Item shape consumed by the criteria engine/preview.
  // split-criteria-ean-atomic: ean13 per blocchi EAN-atomic + residuo multi-operatore.
  const criteriaItems = detail.items.map((it) => ({
    seq: it.seq, qty_required: it.qty_required, location_code: it.location_code ?? '',
    ean13: it.ean13 ?? null,
  }));

  function handleClose(): void {
    if (step > 1 && !confirm(t('wizard.close_confirm'))) return;
    reset();
    onClose();
  }

  function buildCmd(): SplitCmd | null {
    if (mode === 'auto_qty') return { mode: 'auto_qty', operator_user_ids: autoQtyOps };
    if (mode === 'criteria') return buildCriteriaCmd(criteriaDraft);
    if (mode === 'manual') {
      const byOp = new Map<number, Array<{ pick_list_item_id: number; qty_assigned: number }>>();
      for (const item of detail.items) {
        const parts = manualAssignments[item.seq] ?? [];
        for (const p of parts) {
          if (!byOp.has(p.operator_user_id)) byOp.set(p.operator_user_id, []);
          byOp.get(p.operator_user_id)!.push({
            pick_list_item_id: item.pick_list_item_id,
            qty_assigned: p.qty,
          });
        }
      }
      return {
        mode: 'manual',
        missions: Array.from(byOp, ([operator_user_id, tasks]) => ({ operator_user_id, tasks })),
      };
    }
    return null;
  }

  async function submit(): Promise<void> {
    const cmd = buildCmd();
    if (!cmd) return;
    try {
      await split.mutateAsync(cmd);
      reset();
      onClose();
    } catch (err) {
      // The error is surfaced to the user via the `split.isError` banner
      // rendered below (see splitErrorMessage). We still log for debugging,
      // but no longer rely on the console as the only feedback channel.
      console.error(err);
    }
  }

  function canAdvanceStep2(): boolean {
    if (mode === 'auto_qty') return autoQtyOps.length >= 2;
    if (mode === 'manual') {
      // Each row must be fully assigned: sum(part.qty) === qty_required.
      return detail.items.every((it) => {
        const parts = manualAssignments[it.seq] ?? [];
        const sum = parts.reduce((s, p) => s + p.qty, 0);
        return sum === it.qty_required;
      });
    }
    if (mode === 'criteria') return buildCriteriaCmd(criteriaDraft) !== null;
    return false;
  }

  function buildSummary(): ConfirmSummaryRow[] {
    if (mode === 'auto_qty') {
      const totalQty = detail.items.reduce((s, it) => s + it.qty_required, 0);
      return autoQtyOps.map((opId, i) => {
        const op = availableOperators.find((o) => o.id === opId);
        return {
          label: `${t('wizard.summary.mission_to', { n: i + 1, target: op?.label ?? `#${opId}` })}`,
          detail: t('wizard.summary.qty_approx', { qty: Math.round(totalQty / autoQtyOps.length) }),
        };
      });
    }
    if (mode === 'manual') {
      const byOp = new Map<number, { taskCount: number; qty: number }>();
      for (const item of detail.items) {
        const parts = manualAssignments[item.seq] ?? [];
        for (const p of parts) {
          const cur = byOp.get(p.operator_user_id) ?? { taskCount: 0, qty: 0 };
          byOp.set(p.operator_user_id, { taskCount: cur.taskCount + 1, qty: cur.qty + p.qty });
        }
      }
      let i = 0;
      return Array.from(byOp, ([opId, stats]) => {
        const op = availableOperators.find((o) => o.id === opId);
        return {
          label: t('wizard.summary.mission_to', { n: ++i, target: op?.label ?? `#${opId}` }),
          detail: t('wizard.summary.tasks_qty', { tasks: stats.taskCount, qty: stats.qty }),
        };
      });
    }
    if (mode === 'criteria') {
      return simulateCriteria(criteriaItems, criteriaDraft, availableOperators).map((s) => ({
        label: `${s.label} → ${s.assignLabel}`,
        detail: t('wizard.summary.criteria_detail', { titles: s.titles, qty: s.qty, locations: s.locations }),
      }));
    }
    return [];
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title={t('wizard.title', { external_id: detail.pick_list.external_id })}
            backdrop={false} scroll>
      {/* SP-8g — wizard di split NON modale: niente velo (la lista sotto resta
          visibile e scrollabile) e il click fuori non chiude. Si chiude solo
          con × / Annulla (con conferma se ci sono scelte non salvate). */}
      {/* SP-8g §12 — riepilogo lista in testa: aiuta a decidere come splittare. */}
      <div className="d-flex flex-wrap gap-3 mb-3 pb-2 border-bottom small">
        <span>{t('wizard.summary_head.qty')}: <strong>{totalQtyRequired}</strong></span>
        <span className="text-muted">·</span>
        <span>{t('wizard.summary_head.locations')}: <strong>{distinctLocations}</strong></span>
      </div>
      <WizardSteps steps={[t('wizard.steps.mode'), t('wizard.steps.config'), t('wizard.steps.confirm')]} current={step} />
      {step === 1 && <StepModeSelect value={mode} onChange={setMode} />}
      {step === 2 && mode === 'manual' && (
        <ManualModeKanban
          items={detail.items.map((it) => ({
            seq: it.seq, item_external_id: it.item_external_id,
            location_code: it.location_code, qty_required: it.qty_required,
          }))}
          // value/onChange below carry the new partial-assignment shape
          // (Record<seq, Array<{operator_user_id, qty}>>).
          operators={availableOperators}
          value={manualAssignments}
          onChange={setManualAssignments}
        />
      )}
      {step === 2 && mode === 'auto_qty' && (
        <AutoQtyOperatorPicker
          items={detail.items.map((it) => ({ seq: it.seq, qty_required: it.qty_required }))}
          operators={availableOperators}
          value={autoQtyOps}
          onChange={setAutoQtyOps}
        />
      )}
      {step === 2 && mode === 'criteria' && (
        <CriteriaSegmentInput
          items={criteriaItems}
          operators={availableOperators}
          value={criteriaDraft}
          onChange={setCriteriaDraft}
        />
      )}
      {step === 3 && <StepConfirmSplit summary={buildSummary()} />}
      {split.isError && (
        <Alert color="danger" className="mt-3 mb-0" role="alert">
          {splitErrorMessage(split.error, t)}
        </Alert>
      )}
      <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3">
        <Button type="button" color="light" size="sm" onClick={handleClose}>
          {t('wizard.footer.cancel')}
        </Button>
        <div className="d-flex gap-2">
          {step > 1 && (
            <Button type="button" color="light" size="sm" onClick={() => setStep((step - 1) as 1 | 2)}>
              <i className="bi bi-arrow-left align-bottom" /> {t('wizard.footer.back')}
            </Button>
          )}
          {step < 3 && (
            <Button
              type="button" color="primary" size="sm"
              disabled={mode === null || (step === 2 && !canAdvanceStep2())}
              onClick={() => setStep((step + 1) as 2 | 3)}>
              {t('wizard.footer.next')} <i className="bi bi-arrow-right align-bottom" />
            </Button>
          )}
          {step === 3 && (
            <Button type="button" color="primary" size="sm"
                    onClick={submit} disabled={split.isPending}>
              {split.isPending
                ? (<><span className="spinner-border spinner-border-sm me-1" role="status" />{t('wizard.footer.submitting')}</>)
                : (<><i className="bi bi-scissors align-bottom me-1" />{t('wizard.footer.submit')}</>)}
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
}
